import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Sparkles, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 font-display font-bold text-xl mb-8 justify-center">
          <Sparkles className="w-6 h-6" /> SendOccasions
        </Link>
        <div className="nb-card p-8" data-testid="reset-card">
          {!token ? (
            <div className="text-center">
              <h1 className="text-2xl font-display font-bold">Invalid link</h1>
              <p className="text-[#4A4A4A] mt-2">This reset link is missing or broken.</p>
              <Link to="/forgot-password" className="nb-btn bg-[#FFB5A7] px-6 py-3 mt-5 inline-flex">Request a new link</Link>
            </div>
          ) : done ? (
            <div className="text-center">
              <CheckCircle2 className="w-14 h-14 mx-auto text-[#0A0A0A]" />
              <h1 className="text-2xl font-display font-bold mt-4">Password updated!</h1>
              <p className="text-[#4A4A4A] mt-2">Redirecting you to log in…</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h1 className="text-2xl sm:text-3xl font-display font-bold">Set a new password</h1>
              {error && <div className="nb-border rounded-xl bg-[#FFD5CC] px-4 py-3 mt-4 font-semibold text-sm" data-testid="reset-error">{error}</div>}
              <label className="block mt-5">
                <span className="text-xs font-bold uppercase tracking-widest">New password</span>
                <input required type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input"
                  className="w-full nb-border rounded-xl px-4 py-3 bg-[#FDFBF7] mt-1.5 focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" placeholder="••••••••" />
              </label>
              <label className="block mt-4">
                <span className="text-xs font-bold uppercase tracking-widest">Confirm password</span>
                <input required type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input"
                  className="w-full nb-border rounded-xl px-4 py-3 bg-[#FDFBF7] mt-1.5 focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" placeholder="••••••••" />
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none mt-3">
                <input type="checkbox" checked={showPw} onChange={() => setShowPw(!showPw)} data-testid="reset-show-password" className="w-4 h-4 accent-[#FFB5A7]" />
                Show password
              </label>
              <button type="submit" disabled={loading} data-testid="reset-submit" className="nb-btn bg-[#FFB5A7] w-full py-3.5 mt-5">
                {loading ? "Saving…" : "Update password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
